import React, { useEffect, useRef } from 'react';
import '../modal/contac/filtro/ModalFiltro.css';
import '../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface ModalSyncProps {
  titulo: string;
  subtitulo?: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Rodapé com os botões de ação (o fechar do cabeçalho já existe sempre). */
  footer?: React.ReactNode;
  /**
   * Largura do modal. O default é o do modal de filtros do Contac (560px), estreito para
   * formulário; conteúdo largo (tabela) passa um valor maior — ver `JobDetalheModal`.
   */
  largura?: string;
}

/**
 * Casco de modal do painel, montado **inteiramente** com as classes do modal de filtros
 * do Contac (`components/modal/contac/filtro/ModalFiltro.css`), que já servem 5 modais
 * por import — nenhuma classe nova aqui. Regras do padrão: montagem condicional pelo
 * chamador (`{aberto && <ModalSync ...>}`), clique fora por `mousedown` + `ref` e Escape.
 *
 * Para confirmação simples (só texto + confirmar/cancelar) use o `ConfirmModal`
 * compartilhado; este casco existe para os modais com corpo próprio (período, modo do
 * Bloco 3, filtros do histórico e detalhe da execução).
 */
const ModalSync: React.FC<ModalSyncProps> = ({
  titulo,
  subtitulo,
  onClose,
  children,
  footer,
  largura = 'min(560px, 92vw)',
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) onClose();
    };
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('mousedown', handleMouseDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose]);

  return (
    <div className="modal-filtro-overlay">
      {/*
        Duas contenções que os modais de filtro do Contac não precisavam ter (o conteúdo
        deles é sempre um formulário curto) e que este casco precisa, porque hospeda
        tabela e lista longa de etapas:
          - `largura`: o 560px do CSS é estreito para tabela — o detalhe da execução passa
            um valor maior, senão as colunas ficam cortadas pelo `overflow: hidden` do card;
          - `maxHeight` no card + `overflow-y` no corpo: sem isso o card cresce com o
            conteúdo e vaza para fora da tela (o `max-height: 90vh` do CSS está no
            wrapper, que não segura o filho). Cabeçalho e rodapé ficam fixos.
      */}
      <div className="modal-filtro-content" ref={modalRef} style={{ width: largura }}>
        <div className="filtro-modal-card" style={{ maxHeight: '90vh' }}>
          <div className="filtro-modal-header">
            <div>
              <h2 className="filtro-modal-title">{titulo}</h2>
              {subtitulo && <p className="filtro-modal-subtitle">{subtitulo}</p>}
            </div>
            <button className="close-button" type="button" onClick={onClose} aria-label="Fechar">
              &times;
            </button>
          </div>

          <div className="filtro-modal-body" style={{ overflowY: 'auto', minHeight: 0 }}>
            {children}
          </div>

          {footer && <div className="filtro-modal-footer">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

export default ModalSync;
